import { useState, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/hooks/use-toast'
import { GerencialImportStep1 } from './GerencialImportStep1'
import { GerencialImportStep2 } from './GerencialImportStep2'
import {
  useGetUploadUrl,
  useProcessImport,
  usePreviewImport,
  type PreviewImportOutput,
} from '../hooks/useFluxoGerencialMutations'

type Stage =
  | 'select'        // Step 1 — file selection
  | 'previewing'    // upload to S3 + run server-side preview parser
  | 'preview'       // Step 2 — show counts/sample, await Confirm
  | 'processing'    // call processImport (writes DB + triggers DRE)

interface GerencialImportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDownloadTemplate: () => void
  onImportComplete?: () => void
}

/**
 * Two-step import modal:
 *   Step 1 (select): drag/click → file selection
 *   On Próximo: upload to S3 → call previewImport → show Step 2
 *   Step 2 (preview): counts + sample table + DRE warning + confirm
 *   On Confirm: call processImport → toast + close
 *
 * The S3 upload happens BEFORE Step 2 (not on confirm) so the preview can
 * show real parsed counts to the user — same parser is used by processImport
 * so what they see is exactly what gets imported.
 */
export function GerencialImportModal({
  open,
  onOpenChange,
  onDownloadTemplate,
  onImportComplete,
}: GerencialImportModalProps) {
  const [stage, setStage] = useState<Stage>('select')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [s3Key, setS3Key] = useState<string | null>(null)
  const [preview, setPreview] = useState<PreviewImportOutput | null>(null)
  const { error: showError, success } = useToast()

  const getUploadUrl = useGetUploadUrl()
  const previewImport = usePreviewImport()
  const processImport = useProcessImport()

  const isBusy = stage === 'previewing' || stage === 'processing'

  const reset = useCallback(() => {
    setStage('select')
    setSelectedFile(null)
    setS3Key(null)
    setPreview(null)
  }, [])

  const handleClose = (next: boolean) => {
    if (!next && isBusy) return
    if (!next) reset()
    onOpenChange(next)
  }

  const handleNext = async () => {
    if (!selectedFile) return
    try {
      setStage('previewing')
      const { signedUrl, s3Key: key } = await getUploadUrl.mutateAsync({
        fileName: selectedFile.name,
        contentType: selectedFile.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: { 'Content-Type': selectedFile.type },
      })
      if (!uploadResponse.ok) {
        throw new Error(`Upload falhou: ${uploadResponse.statusText}`)
      }
      setS3Key(key)

      const previewResult = await previewImport.mutateAsync({ s3Key: key })
      setPreview(previewResult)
      setStage('preview')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar preview'
      showError(message)
      setStage('select')
    }
  }

  const handleConfirm = async () => {
    if (!selectedFile || !s3Key) return
    try {
      setStage('processing')
      const result = await processImport.mutateAsync({
        s3Key,
        fileName: selectedFile.name,
      })
      success(`${result.rowCount} registros importados · DRE reprocessando em segundo plano`)
      reset()
      onOpenChange(false)
      onImportComplete?.()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro inesperado na importação'
      showError(message)
      setStage('preview') // user can retry confirm against the same s3Key
    }
  }

  const titleByStage: Record<Stage, string> = {
    select: 'Importar Fluxo Gerencial — Passo 1 de 2',
    previewing: 'Analisando arquivo…',
    preview: 'Importar Fluxo Gerencial — Passo 2 de 2',
    processing: 'Processando importação…',
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-brand-dark border-brand-dark-lighter sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-display">
            {titleByStage[stage]}
          </DialogTitle>
          <p className="text-xs text-brand-gray font-body">
            {stage === 'select' && 'Selecione um arquivo para importar'}
            {stage === 'previewing' && 'Enviando e validando o arquivo…'}
            {stage === 'preview' && 'Confira os dados antes de importar'}
            {stage === 'processing' && 'Não feche a aba'}
          </p>
        </DialogHeader>

        <div className="py-2">
          {stage === 'select' && (
            <GerencialImportStep1
              selectedFile={selectedFile}
              onFileSelected={setSelectedFile}
              onClearFile={() => setSelectedFile(null)}
              onDownloadTemplate={onDownloadTemplate}
            />
          )}

          {stage === 'previewing' && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
              <p className="text-sm text-white">Enviando para o servidor e validando…</p>
              <p className="text-xs text-brand-gray text-center max-w-sm">
                O servidor está parseando o arquivo. Nada foi gravado ainda.
              </p>
            </div>
          )}

          {stage === 'preview' && selectedFile && preview && (
            <GerencialImportStep2
              fileName={selectedFile.name}
              fileSize={selectedFile.size}
              preview={preview}
            />
          )}

          {stage === 'processing' && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
              <p className="text-sm text-white">Servidor processando linhas…</p>
              <p className="text-xs text-brand-gray text-center max-w-sm">
                Esta operação dispara o reprocessamento do cache da DRE e pode levar até 1 minuto.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          {stage === 'select' && (
            <>
              <Button variant="ghost" onClick={() => handleClose(false)}>Cancelar</Button>
              <Button
                variant="primary"
                disabled={!selectedFile}
                onClick={handleNext}
              >
                Próximo →
              </Button>
            </>
          )}

          {stage === 'previewing' && (
            <Button variant="ghost" disabled>Aguarde…</Button>
          )}

          {stage === 'preview' && preview && (
            <>
              <Button variant="ghost" onClick={() => { setStage('select'); setS3Key(null); setPreview(null) }}>
                ← Voltar
              </Button>
              <Button variant="ghost" onClick={() => handleClose(false)}>Cancelar</Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={preview.validRows === 0}
              >
                ✓ Importar {preview.validRows} {preview.validRows === 1 ? 'linha' : 'linhas'}
              </Button>
            </>
          )}

          {stage === 'processing' && (
            <Button variant="ghost" disabled>Aguarde…</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
